import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import { MapPin, Droplets, Calendar, Wrench } from 'lucide-react';
import { formatCurrency, STATUS_CONFIG, PaymentBadge } from '../utils/helpers';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getKanbanColumns, ALL_STATUS_COLORS } from '../config/businessTypes';

// Default fallback columns (laundry)
const DEFAULT_COLUMNS = [
  { id: 'masuk',        label: { id: 'Masuk',        en: 'Received'   }, color: 'blue'    },
  { id: 'proses_cuci',  label: { id: 'Diproses',     en: 'Processing' }, color: 'yellow'  },
  { id: 'siap_diambil', label: { id: 'Siap Diambil', en: 'Ready'      }, color: 'emerald' },
  { id: 'selesai',      label: { id: 'Selesai',      en: 'Done'       }, color: 'slate'   },
];

function OrderCard({ order, index, lang, adminPath, formType }) {
  const navigate = useNavigate();
  return (
    <Draggable draggableId={order.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => navigate(`${adminPath}/orders/${order.id}`)}
          data-testid={`kanban-card-${order.id}`}
          className={`bg-white rounded-xl border border-slate-100 p-3.5 mb-2 cursor-pointer select-none
            ${snapshot.isDragging ? 'shadow-xl rotate-1 scale-[1.02] ring-2 ring-[#0f6c44]/20' : 'shadow-sm hover:shadow-md hover:-translate-y-0.5'}
            transition-all duration-150`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-xs font-bold text-[#0f6c44] bg-emerald-50 px-2 py-0.5 rounded-lg">
              {order.tracking_code}
            </span>
            <PaymentBadge status={order.payment_status} lang={lang} />
          </div>
          <p className="font-semibold text-slate-800 text-sm mb-1.5 truncate">{order.customer_name || '-'}</p>

          {/* Items — render differently per form type */}
          <div className="space-y-0.5 mb-2">
            {formType === 'servis' ? (
              // Servis: show device name + condition snippet
              <p className="text-xs text-slate-500 truncate">
                {(order.items || []).map(i => i.product_name || i.device_name).filter(Boolean).join(', ') || order.notes || '-'}
              </p>
            ) : formType === 'rental' ? (
              // Rental: show unit + dates
              <>
                <p className="text-xs text-slate-500 truncate">{(order.items || []).map(i => i.product_name).join(', ')}</p>
                {order.rental_start && (
                  <span className="flex items-center gap-1 text-xs text-slate-400"><Calendar size={10} />{order.rental_start}</span>
                )}
              </>
            ) : (
              // Default (laundry, appointment)
              <>
                {(order.items || []).slice(0, 2).map((item, i) => (
                  <p key={i} className="text-xs text-slate-500 truncate">{item.product_name} · {item.quantity} {item.unit}</p>
                ))}
                {(order.items?.length || 0) > 2 && <p className="text-xs text-slate-400">+{order.items.length - 2} lainnya</p>}
              </>
            )}
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-slate-50">
            {order.shelf_location && (
              <span className="flex items-center gap-1 text-xs text-slate-500"><MapPin size={10} />{order.shelf_location}</span>
            )}
            {order.parfum && (
              <span className="flex items-center gap-1 text-xs text-slate-500"><Droplets size={10} />{order.parfum}</span>
            )}
            {formType === 'servis' && order.total_amount === 0 && (
              <span className="flex items-center gap-1 text-xs text-orange-500"><Wrench size={10} />TBD</span>
            )}
            <span className="ml-auto text-xs font-bold text-slate-700">
              {order.total_amount > 0 ? formatCurrency(order.total_amount) : <span className="text-orange-500 font-semibold">Estimasi TBD</span>}
            </span>
          </div>
        </div>
      )}
    </Draggable>
  );
}

export default function KanbanBoard({ orders = [], onStatusChange, isLoading, adminPath = '/admin', columns: colsProp, cardLimit = 5 }) {
  const { lang } = useLanguage();
  const { businessType } = useAuth();

  // Use passed columns, or auto-detect from businessType, or fallback to default
  const columns = colsProp || getKanbanColumns(businessType) || DEFAULT_COLUMNS;

  const { store } = useAuth();
  const formType = store?.business_type
    ? (store.business_type.startsWith('servis') ? 'servis' : store.business_type === 'rental' ? 'rental' : 'default')
    : 'default';

  const grouped = columns.reduce((acc, col) => {
    acc[col.id] = orders.filter(o => o.status === col.id);
    return acc;
  }, {});

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.droppableId === result.source.droppableId) return;
    onStatusChange?.(result.draggableId, result.destination.droppableId);
  };

  if (isLoading) {
    return (
      <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4">
        {columns.map(col => (
          <div key={col.id} className="w-72 flex-shrink-0 space-y-2">
            <div className="h-10 bg-slate-200 rounded-xl animate-pulse" />
            {[1, 2].map(i => <div key={i} className="h-28 bg-slate-100 rounded-xl animate-pulse" />)}
          </div>
        ))}
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto kanban-scroll pb-4 min-h-[65vh]" data-testid="kanban-board">
        {columns.map(col => {
          const statusCfg = STATUS_CONFIG[col.id];
          const colorCfg = ALL_STATUS_COLORS[col.color] || ALL_STATUS_COLORS.blue;
          const colOrders = grouped[col.id] || [];
          return (
            <div key={col.id} className="w-72 flex-shrink-0 flex flex-col">
              <div className={`flex items-center justify-between mb-3 px-3 py-2.5 rounded-xl bg-white border border-slate-100 border-l-4 ${statusCfg?.border || colorCfg.border} shadow-sm`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${statusCfg?.dot || colorCfg.dot}`} />
                  <span className="font-semibold text-slate-700 text-sm">{col.label[lang]}</span>
                </div>
                <span className="bg-slate-100 text-slate-600 text-xs font-bold px-2 py-0.5 rounded-full">{colOrders.length}</span>
              </div>
              <Droppable droppableId={col.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`flex-1 p-2 rounded-xl min-h-[200px] transition-colors duration-200 ${
                      snapshot.isDraggingOver ? 'bg-emerald-50 border-2 border-emerald-300 border-dashed' : 'bg-slate-50/50'
                    }`}
                  >
                    {colOrders.slice(0, cardLimit).map((order, index) => (
                      <OrderCard key={order.id} order={order} index={index} lang={lang} adminPath={adminPath} formType={formType} />
                    ))}
                    {provided.placeholder}
                    {colOrders.length === 0 && !snapshot.isDraggingOver && (
                      <div className="flex items-center justify-center h-24 text-slate-400 text-xs">
                        {lang === 'id' ? 'Tidak ada pesanan' : 'No orders'}
                      </div>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
